import { useState, useEffect, Dispatch, SetStateAction } from 'react'

/**
 * useValueEditor
 * Lightweight hook that:
 * 1. Maintains a local editable representation of an external value (e.g. string -> number, object expansion).
 * 2. Keeps local state in sync when the upstream value prop changes.
 * 3. Applies parseValue (external -> internal) and serializeValue (internal -> external) transformations.
 * 4. Emits onChange with a serialized external value whenever localValue mutates.
 *
 * Explicitly DOES NOT decide when a value should be committed/persisted. That responsibility
 * belongs to useCommitController (paired via saveTriggers) or a higher‑level form submit.
 *
 * Recommended usage pattern inside an editor component:
 * const { localValue, handleLocalChange } = useValueEditor({ value, parseValue, serializeValue, onChange })
 * // Render UI bound to localValue, call handleLocalChange on user edits.
 */

interface UseValueEditorParams<ExternalValue, InternalValue> {
  value: ExternalValue
  parseValue: (val: ExternalValue) => InternalValue
  serializeValue: (val: InternalValue) => ExternalValue
  onChange?: (data: { value: ExternalValue }) => void
}

interface UseValueEditorReturn<InternalValue, _ExternalValue> {
  localValue: InternalValue
  setLocalValue: Dispatch<SetStateAction<InternalValue>>
  handleLocalChange: (newValue: InternalValue) => void
}

function useValueEditor<ExternalValue, InternalValue>({
  value,
  parseValue,
  serializeValue,
  onChange,
}: UseValueEditorParams<ExternalValue, InternalValue>): UseValueEditorReturn<InternalValue, ExternalValue> {
  const [localValue, setLocalValue] = useState<InternalValue>(parseValue(value))

  useEffect(() => {
    setLocalValue(parseValue(value))
  }, [value, parseValue])

  const handleLocalChange = (newValue: InternalValue) => {
    setLocalValue(newValue)
    if (onChange) {
      onChange({ value: serializeValue(newValue) })
    }
  }

  return { localValue, setLocalValue, handleLocalChange }
}

export default useValueEditor
